import type { EntryKey } from "@pinia/colada";
import { useQuery } from "@pinia/colada";
import { subscriptionLimitsApi } from "@/api/subscription-limits.api";

export const SUBSCRIPTION_LIMITS_QUERY_KEYS = {
  root: ["subscription-limits"] as const,
  all: () => [...SUBSCRIPTION_LIMITS_QUERY_KEYS.root, "all"] as const,
};

/**
 * Query hook for fetching all subscription limits
 *
 * Features:
 * - Fetches all resource limits and feature flags in one call
 * - Smart caching with 5min stale time
 * - Used for displaying the limits dashboard (OrganizationPlan page)
 *
 * @example
 * ```ts
 * const { data, isLoading, error } = useSubscriptionLimitsQuery();
 * // data.limits - array of resource usage stats
 * // data.features - array of feature flags
 * // data.subscription - current plan name
 * ```
 */
export function useSubscriptionLimitsQuery() {
  return useQuery({
    key: () => SUBSCRIPTION_LIMITS_QUERY_KEYS.all() as unknown as EntryKey,
    query: async () => {
      return await subscriptionLimitsApi.getLimits();
    },
    staleTime: 5 * 60_000, // 5 minutes - limits don't change frequently
    gcTime: 10 * 60_000, // 10 minutes - keep in cache
  });
}
